import { Refine, Authenticated } from '@refinedev/core';
import { createFilterApiDataProvider } from './data-providers/filter-api';
import {
  useNotificationProvider,
  ErrorComponent,
  ThemedLayout,
  RefineThemes,
} from '@refinedev/antd';
import { ConfigProvider, App as AntdApp } from 'antd';
import heIL from 'antd/locale/he_IL';
import enUS from 'antd/locale/en_US';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import { BrowserRouter, Routes, Route, Outlet, Navigate } from 'react-router';
import routerProvider, { NavigateToResource } from '@refinedev/react-router';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import '@refinedev/antd/dist/reset.css';
import { useHtmlDirSync } from './use-html-dir';

import { authProvider } from './auth-provider';
import { accessControlProvider } from './access-control';
import { HeaderControls } from './header-controls';
import { LoginPage } from './pages/login';
import { CommunityList, CommunityCreate, CommunityShow } from './pages/communities';
import {
  OrganizationList,
  OrganizationCreate,
  OrganizationShow,
} from './pages/organizations';
import { UserList, UserCreate, UserEdit, UserShow } from './pages/users';
import { GroupList, GroupCreate, GroupEdit, GroupShow } from './pages/groups';
import { CommunityDashboard, OrganizationDashboard } from './pages/dashboards';
import { PolicyEditor } from './pages/policy-editor';
import { AuditLogList, AuditLogShow } from './pages/audit-log';
import { ModerationQueueList } from './pages/moderation-queue';

// One stable DataProvider instance — avoids re-creating on every <App /> render.
const filterApiDataProvider = createFilterApiDataProvider();

export function App() {
  const { i18n, t } = useTranslation();
  const { direction, isHe } = useHtmlDirSync(i18n.language);
  const locale = isHe ? heIL : enUS;

  useEffect(() => {
    dayjs.locale(isHe ? 'he' : 'en');
  }, [isHe]);

  return (
    <BrowserRouter>
      <ConfigProvider direction={direction} locale={locale} theme={RefineThemes.Blue}>
        <AntdApp>
          <Refine
            dataProvider={filterApiDataProvider}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            routerProvider={routerProvider}
            notificationProvider={useNotificationProvider}
            resources={[
              {
                name: 'communities',
                list: '/communities',
                create: '/communities/create',
                // edit: deferred to Story 3.7c (needs PATCH /v1/communities/:id).
                show: '/communities/:id',
                meta: { label: t('resources.communities') },
              },
              {
                name: 'organizations',
                list: '/communities/:communityId/organizations',
                create: '/communities/:communityId/organizations/create',
                // edit: deferred to Story 3.7c (needs PATCH /v1/organizations/:id).
                show: '/communities/:communityId/organizations/:id',
                meta: { parent: 'communities', label: t('resources.organizations') },
              },
              {
                name: 'users',
                list: '/users',
                create: '/users/create',
                edit: '/users/:id/edit',
                show: '/users/:id',
                meta: { label: t('resources.users') },
              },
              {
                name: 'groups',
                list: '/organizations/:organizationId/groups',
                create: '/organizations/:organizationId/groups/create',
                edit: '/organizations/:organizationId/groups/:id/edit',
                show: '/organizations/:organizationId/groups/:id',
                meta: { parent: 'organizations', label: t('resources.groups') },
              },
              {
                name: 'audit-log',
                list: '/communities/:communityId/audit-log',
                show: '/audit-log/:id',
                meta: { parent: 'communities', label: t('resources.auditLog') },
              },
              {
                name: 'moderation-queue',
                list: '/communities/:communityId/moderation-queue',
                meta: { parent: 'communities', label: t('resources.moderationQueue') },
              },
              // Story 3.7d — virtual resources (routes-only, no CRUD via data provider).
              {
                name: 'community-dashboard',
                list: '/communities/:communityId/dashboard',
                meta: { parent: 'communities', label: t('resources.communityDashboard') },
              },
              {
                name: 'organization-dashboard',
                list: '/communities/:communityId/organizations/:organizationId/dashboard',
                meta: { parent: 'organizations', label: t('resources.organizationDashboard') },
              },
            ]}
            options={{ syncWithLocation: true, warnWhenUnsavedChanges: true }}
          >
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <Authenticated key="protected" fallback={<Navigate to="/login" replace />}>
                    <ThemedLayout Header={() => <HeaderControls />}>
                      <Outlet />
                    </ThemedLayout>
                  </Authenticated>
                }
              >
                <Route path="/" element={<NavigateToResource resource="communities" />} />
                <Route path="/communities" element={<CommunityList />} />
                <Route path="/communities/create" element={<CommunityCreate />} />
                <Route path="/communities/:id" element={<CommunityShow />} />
                {/* /communities/:id/edit — deferred to Story 3.7c. */}
                {/* Story 3.9 — moderation queue. */}
                <Route path="/communities/:communityId/moderation-queue" element={<ModerationQueueList scope="community" />} />
                {/* Story 3.8 — audit log viewer (community-scope). */}
                <Route path="/communities/:communityId/audit-log" element={<AuditLogList scope="community" />} />
                <Route path="/communities/:id/policy" element={<PolicyEditor />} />
                <Route
                  path="/communities/:communityId/organizations"
                  element={<OrganizationList />}
                />
                <Route
                  path="/communities/:communityId/organizations/create"
                  element={<OrganizationCreate />}
                />
                <Route
                  path="/communities/:communityId/organizations/:id"
                  element={<OrganizationShow />}
                />
                {/* /communities/:communityId/organizations/:id/edit — deferred to Story 3.7c. */}
                <Route path="/users" element={<UserList />} />
                <Route path="/users/create" element={<UserCreate />} />
                <Route path="/users/:id" element={<UserShow />} />
                <Route path="/users/:id/edit" element={<UserEdit />} />
                <Route path="/organizations/:organizationId/groups" element={<GroupList />} />
                <Route
                  path="/organizations/:organizationId/groups/create"
                  element={<GroupCreate />}
                />
                <Route path="/organizations/:organizationId/groups/:id" element={<GroupShow />} />
                <Route
                  path="/organizations/:organizationId/groups/:id/edit"
                  element={<GroupEdit />}
                />
                {/* Story 3.7d — dashboards. */}
                <Route
                  path="/communities/:communityId/dashboard"
                  element={<CommunityDashboard />}
                />
                <Route
                  path="/communities/:communityId/organizations/:organizationId/dashboard"
                  element={<OrganizationDashboard />}
                />
                <Route
                  path="/communities/:communityId/organizations/:organizationId/moderation-queue"
                  element={<ModerationQueueList scope="organization" />}
                />
                {/* Story 3.8 — audit log (org-scope + detail). */}
                <Route
                  path="/communities/:communityId/organizations/:organizationId/audit-log"
                  element={<AuditLogList scope="organization" />}
                />
                <Route path="/audit-log/:id" element={<AuditLogShow />} />
                <Route path="*" element={<ErrorComponent />} />
              </Route>
            </Routes>
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </BrowserRouter>
  );
}
